import { Option } from '@/components/inputs/AutocompleteInput';
import { useGetDriversQuery } from '../services';
import { useMemo } from 'react';
import {
  useAutocompleteSearch,
  useStableOptions,
} from '@/hooks/useAutocompleteSearch';

interface UseDriversAutocompleteProps {
  search?: string;
  minLength?: number;
  logisticOperatorId?: string;
}

const useDriversAutocomplete = ({
  search,
  minLength = 3,
  logisticOperatorId,
}: UseDriversAutocompleteProps = {}) => {
  const { debouncedSearch, shouldSearch, isDebouncePending } =
    useAutocompleteSearch(search, minLength);

  const {
    data: driversData,
    isFetching: isDriversFetching,
    error: driversError,
  } = useGetDriversQuery(
    {
      paginated: { page: 1, pageSize: 1000 },
      filters: {
        search: shouldSearch ? debouncedSearch : '',
        ...(logisticOperatorId?.trim()
          ? { assignableToRouteOperatorId: logisticOperatorId.trim() }
          : {}),
      },
    },
    { skip: !shouldSearch }
  );

  const isDriversLoading = shouldSearch && isDriversFetching;

  const resolvedDriverOptions: Option[] = useMemo(() => {
    if (!driversData?.items) return [];

    return driversData.items.map((driver) => ({
      label: `${driver.firstName} ${driver.lastName}`.trim() || driver.id,
      data: driver.id,
    }));
  }, [driversData?.items]);

  const driverOptions = useStableOptions(resolvedDriverOptions, {
    isPending: isDriversFetching || isDebouncePending,
    enabled: shouldSearch || isDebouncePending,
  });

  return {
    driverOptions,
    isDriversLoading,
    driversError,
  };
};

export default useDriversAutocomplete;
